import { alphabeticalOrder } from "@arenko-group/arenko-ui/dist/helpers";
import { arenkoChartServices } from "@arenko-group/arenko-ui/dist/theme/ArenkoTheme";
import { createSelector } from "@reduxjs/toolkit";
import TableRowModel from "@/components/AgGrid/AgGridRow.model";
import {
  CURRENT_TIME,
  eightWeeksInMilliseconds,
  excludedStates,
} from "@/enums/chart.enum";
import { websocketAdapters } from "@/features/powerRootSlice/services/webSocket/websocketConfig";
import { websocketQuery } from "@/features/powerRootSlice/services/webSocket/websocketQuery";
import {
  absolutesToArea,
  absolutesToPoints,
  getFilteredData,
  showCurrentTime,
} from "@/pages/Power/helpers/PowerChart.helpers";
import type { RootState } from "@/store";
import { dynamicSlicesNames } from "@/store.constants";
import type { SelectedOutputObj } from "@/types/data/redux.types";
import type {
  ArenkoChartServicesKey,
  CustomSeriesOptionsType,
  PlotData,
  SchedulePlotData,
  TimeRange,
} from "@/types/data/schedule.types";
import { selectGlobalCurrentTime } from "../globalStateSlice/globalState.selectors";
import type { ScheduleExchangeRootStateType } from ".";
import { scheduleExchangeSliceName } from "./powerSlice";

const baseSelector = (state: RootState) => state;

const scheduleExchangeBaseSelector = createSelector(
  baseSelector,
  (state: RootState): ScheduleExchangeRootStateType =>
    state[dynamicSlicesNames.POWER]!
);

const scheduleBaseSelector = createSelector(
  scheduleExchangeBaseSelector,
  (state) => state?.[scheduleExchangeSliceName]
);

export const selectTypes = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.types
);

export const selectSelectedResource = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.selectedResource
);

export const selectOutputs = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.outputs
);

export const selectSelectedOutput = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.selectedOutput
);

export const selectSelectedRange = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.selectedRange
);

export const selectInactiveLegends = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.inactiveLegends
);

export const selectLiveRange = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.liveRange
);

export const selectedOutputRoot = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.selectedOutput
);
export const selectZoomRange = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.zoomRange
);
export const selectIsZoomed = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.isZoomed
);
export const selectSchedulesDataLoading = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.schedulesDataLoading
);

//return all data that fulfills the criteria of
//the correct asset (websocketquery)
//the correct output
//within the correct time range
export const selectRelevantScheduleData = createSelector(
  [websocketQuery, selectSelectedRange, selectSelectedOutput],
  (data, selectedRange, selectedOutput) => {
    const plots: SchedulePlotData | [] = data
      ? websocketAdapters.scheduleData
          .getSelectors()
          .selectEntities(data.scheduleData)
      : [];
    return Object.values(plots).filter((plot: PlotData) => {
      const { output, timeRange: plotTimeRange } = plot;
      const startsAfterOrFinishesBeforeTimeRange =
        plotTimeRange?.from >
          new Date(selectedRange?.to ?? new Date()).getTime() ||
        plotTimeRange?.to <
          new Date(selectedRange?.from ?? new Date()).getTime();
      return output === selectedOutput && !startsAfterOrFinishesBeforeTimeRange;
    });
  }
);

//using the relevant chart data above, create plots that exclude those in unwanted states (such as deleted)
export const selectRelevantChartPlots = createSelector(
  selectRelevantScheduleData,
  (data) => {
    return data.filter(
      (plot: PlotData) =>
        !excludedStates.includes(plot.state as "rejected" | "deleted")
    );
  }
);

//transform the chart plots
export const selectTransformedChartPlots = createSelector(
  [selectRelevantChartPlots, selectInactiveLegends],
  (data, inactiveLegends) => {
    //only perform the data transform on filtered results
    //give all series not intended to be shown in tooltip the name of 'undefined' otherwise highcharts will auto assign
    const transformedData = data.reduce(
      (
        curr: CustomSeriesOptionsType[],
        plot: PlotData
      ): CustomSeriesOptionsType[] => [
        ...curr,
        {
          name: plot.service,
          type: "line",
          color:
            arenkoChartServices?.[plot.service as ArenkoChartServicesKey]
              ?.borderColor,
          data: absolutesToPoints(plot.absolutes),
          visible: !inactiveLegends?.includes(plot.service),
          //TODO: Commenting out as not part of line series options in highcharts. Test the effect of this.
          // includeInTooltip: true,
          scheduleData: plot.correspondingSchedule,
          marker: {
            enabled: true,
            symbol: "circle",
            radius: 2,
          },
          zIndex: 1000,
          findNearestPointBy: "xy",
        },
        {
          name: plot.service,
          type: "arearange",
          color: "transparent",
          fillColor:
            arenkoChartServices?.[plot.service as ArenkoChartServicesKey]
              ?.backgroundColor,
          data: absolutesToArea(plot.absolutes, plot.bases),
          visible: !inactiveLegends?.includes(plot.service),
          marker: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
          enableMouseTracking: false,
        },
      ],
      [] as CustomSeriesOptionsType[]
    );
    return Array.isArray(transformedData) ? transformedData : [];
  }
);

export const selectZoomState = createSelector(
  scheduleBaseSelector,
  (schedule) => ({
    isZoomed: schedule?.isZoomed,
    zoomRange: schedule?.zoomRange,
  })
);

export const selectTransformedTableRows = createSelector(
  [selectRelevantScheduleData, selectZoomState],
  (scheduleData, { isZoomed, zoomRange }) => {
    const filteredDataForZoom = getFilteredData(
      scheduleData,
      isZoomed,
      zoomRange ?? { min: 0, max: 0 }
    );
    return filteredDataForZoom.map((dat) => new TableRowModel(dat));
  }
);

///////
export const yAxisRange = createSelector(websocketQuery, (data) => data?.range);

export const selectCurrentViewLegends = createSelector(
  [selectRelevantChartPlots, selectLiveRange],
  (data, liveRange) => {
    const legends = new Set();
    data.forEach((plot: PlotData) => {
      legends.add(plot.service);
    });
    const additionalLegends = ["resultant"];
    const timeRange = {
      from: liveRange?.from ? new Date(liveRange.from).getTime() : Date.now(),
      to: liveRange?.to ? new Date(liveRange.to).getTime() : Date.now(),
    };
    if (showCurrentTime(Date.now(), timeRange))
      additionalLegends.push(CURRENT_TIME);
    const sortedLegends: string[] = alphabeticalOrder(Array.from(legends));
    return [...sortedLegends, ...additionalLegends];
  }
);

export const xAxisTickIntervalFormat = createSelector(
  [selectLiveRange],
  (liveRange) => {
    let format = "%H:%M";
    if (
      liveRange?.to &&
      liveRange?.from &&
      new Date(liveRange.to).getTime() - new Date(liveRange.from).getTime() <
        eightWeeksInMilliseconds
    ) {
      format = "%H:%M";
    } else {
      format = "%e %b";
    }

    return format;
  }
);

const defaultRange = 50;

export const selectCurrentTime = createSelector(
  [yAxisRange, selectInactiveLegends, selectLiveRange, selectGlobalCurrentTime],
  (yRange, inactiveLegends, liveRange, currentTime) => {
    const timeRange: TimeRange<number> = {
      from: liveRange?.from ? new Date(liveRange.from).getTime() : Date.now(),
      to: liveRange?.to ? new Date(liveRange.to).getTime() : Date.now(),
    };

    return {
      name: CURRENT_TIME,
      zIndex: 2000,
      type: "line",
      data: showCurrentTime(Date.now(), timeRange)
        ? [
            {
              x: currentTime ?? Date.now(),
              y: (yRange?.min ?? -defaultRange) - 10,
            },
            {
              x: currentTime ?? Date.now(),
              y: (yRange?.max ?? defaultRange) + 10,
            },
          ]
        : [],
      color: "#000000",
      enableMousetracking: false,
      marker: {
        enabled: true,
        symbol: "circle",
      },
      visible: !(inactiveLegends ?? []).includes(CURRENT_TIME),
      states: {
        hover: {
          enabled: false,
        },
      },
    };
  }
);

export const selectedOutput = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.selectedOutput
);

export const outputs = createSelector(
  scheduleBaseSelector,
  (schedule) => schedule?.outputs
);

export const selectedOutputObj = createSelector(
  [outputs, selectedOutput],
  (outputs: SelectedOutputObj[] | undefined, selectedOutput) =>
    outputs ? outputs.find((output) => output.key === selectedOutput) : null
);

export const selectResultant = createSelector(
  [
    websocketQuery,
    selectSelectedOutput,
    selectSelectedRange,
    selectInactiveLegends,
  ],
  (data, output, dateRange, inactiveLegends) => {
    const resultantPlots =
      absolutesToPoints(
        websocketAdapters?.resultants
          ?.getSelectors()
          ?.selectById(
            data?.resultants || websocketAdapters.resultants.getInitialState(),
            `${new Date(dateRange?.from ?? new Date()).getTime()}${new Date(
              dateRange?.to ?? new Date()
            ).getTime()}`
          )?.[output ?? ""]
      ) ?? [];
    return {
      name: "resultant",
      type: "line",
      data: resultantPlots,
      color: arenkoChartServices?.resultant?.backgroundColor,
      visible: !inactiveLegends?.includes("resultant"),
      zIndex: 1000,
      enableMousetracking: false,
      marker: {
        enabled: false,
      },
      states: {
        hover: {
          enabled: false,
        },
      },
    };
  }
);
